import {
  casLocalDraftWithPhotos,
  deleteLocalDraftWithPhotosIfStamp,
  enqueueSubmissionWithPhotos,
  getSubmission,
  listPhotoBlobsByCsid,
  listStuck,
  localDraftStampOf,
  MAX_RETRIES,
  discardSubmission,
} from '../lib/offlineQueue.js';
import type {LocalDraftStamp} from '../lib/offlineQueue.js';
import {newClientSubmissionId} from '../lib/clientSubmissionId.js';
import {lastAuthenticatedUserId} from '../lib/authIdentityMemory.js';
import {queuedReportActor} from '../lib/queuedReportApi.js';
import {recoveryText, type QueuedReportRecovery} from '../lib/offlineSubmissionRecovery.js';
import type {SupabaseClient} from '../lib/supabaseTypes.js';
import {isPristineForm} from './duplicateRecovery.js';

export const HUB_DRAFT_KIND = 'webform_hub_draft';
export type HubForm = 'broiler' | 'layer' | 'egg' | 'pig' | 'cattle' | 'sheep';
export type HubRecord = Record<string, any>;
export type HubDraft = {
  activeForm: HubForm | null;
  forms: Record<HubForm, HubRecord>;
  extras: Record<'broiler' | 'layer' | 'pig', HubRecord[]>;
  photos: Record<Exclude<HubForm, 'egg'>, File[]>;
  done: boolean;
  doneState: string;
  lastGroup: string;
  anchorFailures: Array<{label: string; count: number; date: string; reason: string}>;
};
export type PreservedHubDraft = {csid: string; signature: string};
const PHOTO_FORMS = ['broiler', 'layer', 'pig', 'cattle', 'sheep'] as const;

function object(value: unknown): value is HubRecord {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

// The one canonical form and digest both draft writers hash. Keys are sorted
// recursively so content equality is structural, and both mechanisms
// (preserveHubDraft's signature and the stamped CAS save's content hash)
// compute byte-identical values for identical drafts.
function sortedHubDraftJson(value: unknown): string {
  return JSON.stringify(value, (_key, item) =>
    object(item)
      ? Object.fromEntries(
          Object.keys(item)
            .sort()
            .map((key) => [key, item[key]]),
        )
      : item,
  );
}

async function sha256Hex(text: string): Promise<string> {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))), (v) =>
    v.toString(16).padStart(2, '0'),
  ).join('');
}

// Digests are cached by the actual File object, so unchanged original photos
// are hashed once per session — never once per keystroke's save.
const photoDigestCache = new WeakMap<File, {digest: string; size: number; lastModified: number}>();
async function fileSha256Hex(file: File): Promise<string> {
  const cached = photoDigestCache.get(file);
  if (cached && cached.size === file.size && cached.lastModified === file.lastModified) return cached.digest;
  const digest = await sha256HexBytes(await file.arrayBuffer());
  photoDigestCache.set(file, {digest, size: file.size, lastModified: file.lastModified});
  return digest;
}

async function sha256HexBytes(buffer: ArrayBuffer): Promise<string> {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', buffer)), (v) =>
    v.toString(16).padStart(2, '0'),
  ).join('');
}

export async function assertHubDraftActor(sb: SupabaseClient, expectedActor: string): Promise<void> {
  if (!expectedActor || (await queuedReportActor(sb)) !== expectedActor)
    throw new Error('The signed-in account changed. Your saved draft and photos were retained.');
}

// Local review only. This kind has no flat/RPC registry entry and is never
// eligible for automatic submission. Snapshot and ORIGINAL bytes commit in
// the existing queue's single transaction; no Storage or server write occurs.
export async function preserveHubDraft(
  sb: SupabaseClient,
  snapshot: HubDraft,
  previous: PreservedHubDraft | null,
  expectedActor?: string,
): Promise<PreservedHubDraft> {
  const actor = await queuedReportActor(sb);
  if (!actor) throw new Error('Sign in before leaving this draft. Its answers are still on the form.');
  if (expectedActor && actor !== expectedActor)
    throw new Error('The signed-in account changed. Your saved draft and photos were retained.');
  // NO submission id is minted here. Ids belong to writes, not to events: an
  // unchanged call must be able to return through either dedup path below
  // without minting anything. The id (and the csid-derived photo paths, which
  // only the write needs) are built just before the enqueue.
  const photos = PHOTO_FORMS.flatMap((form) =>
    snapshot.photos[form].map((file, index) => ({
      photo_key: `${form}-${index}`,
      blob: file,
      name: file.name,
      mime: file.type,
      size_bytes: file.size,
      captured_at: new Date(file.lastModified || 0).toISOString(),
    })),
  );
  const signatures = await Promise.all(
    photos.map(async (photo) => ({
      ...photo,
      blob: undefined,
      digest: await fileSha256Hex(photo.blob),
    })),
  );
  const payload = {
    ...snapshot,
    photos: undefined,
    photoKeys: Object.fromEntries(
      PHOTO_FORMS.map((form) => [form, snapshot.photos[form].map((_, index) => `${form}-${index}`)]),
    ),
    version: 1,
  };
  const signature = await sha256Hex(sortedHubDraftJson({actor, payload, photos: signatures}));
  if (previous?.signature === signature) {
    const row = await getSubmission(previous.csid);
    if (row?.form_kind === HUB_DRAFT_KIND && row.actor_profile_id === actor) {
      await assertHubDraftActor(sb, actor);
      return previous;
    }
  }
  // The form may have unmounted after Open. Reuse the same durable snapshot
  // on return, rather than creating another copy solely because refs reset.
  const existing = (await listStuck(HUB_DRAFT_KIND)).find(
    (row) => row.actor_profile_id === actor && object(row.record) && row.record.signature === signature,
  );
  await assertHubDraftActor(sb, actor);
  if (existing) return {csid: existing.csid, signature};
  // Dedup is exhausted: THIS call writes, so it mints the one id it needs and
  // derives its photo paths from it.
  const csid = newClientSubmissionId();
  const enqueuePhotos = photos.map((photo) => ({
    ...photo,
    path: `${HUB_DRAFT_KIND}/${csid}/${photo.photo_key}`,
  }));
  await enqueueSubmissionWithPhotos({
    formKind: HUB_DRAFT_KIND,
    csid,
    actorProfileId: actor,
    payload,
    record: {local_draft: true, signature},
    photos: enqueuePhotos,
    status: 'failed',
    retryCount: MAX_RETRIES,
    lastError: 'Saved on this device for review. Open to continue; nothing is sent automatically.',
  });
  return {csid, signature};
}

export async function listHubDrafts(sb: SupabaseClient) {
  const actor = await queuedReportActor(sb);
  if (!actor) return [];
  const rows = (await listStuck(HUB_DRAFT_KIND)).filter((row) => row.actor_profile_id === actor);
  await assertHubDraftActor(sb, actor);
  return rows;
}

export async function discardHubDraft(
  sb: SupabaseClient,
  csid: string,
  opts: {stillCurrent?: () => boolean} = {},
): Promise<void> {
  const initialMemoryActor = lastAuthenticatedUserId();
  const actor = await queuedReportActor(sb);
  const row = await getSubmission(csid);
  if (!actor || row?.form_kind !== HUB_DRAFT_KIND || row.actor_profile_id !== actor)
    throw new Error('Sign in with the account that saved this draft. Its answers and photos were retained.');
  const stillCurrent = () => {
    if (opts.stillCurrent && !opts.stillCurrent()) return false;
    const currentMemoryActor = lastAuthenticatedUserId();
    if (currentMemoryActor !== null) return currentMemoryActor === actor;
    if (initialMemoryActor !== null) return false;
    return true;
  };
  // Re-read auth after the asynchronous queue read, immediately before the
  // existing atomic row/photo removal. A stale UI row never grants ownership.
  await assertHubDraftActor(sb, actor);
  if (!stillCurrent()) {
    throw new Error('The signed-in account changed. Your saved draft and photos were retained.');
  }
  const removed = await discardSubmission(csid, {actorProfileId: actor, stillCurrent});
  if (!removed) {
    if (!stillCurrent()) {
      throw new Error('The signed-in account changed. Your saved draft and photos were retained.');
    }
    throw new Error('Sign in with the account that saved this draft. Its answers and photos were retained.');
  }
}

export async function loadHubDraft(sb: SupabaseClient, csid: string): Promise<HubDraft> {
  const actor = await queuedReportActor(sb);
  const row = await getSubmission(csid);
  if (!actor || row?.actor_profile_id !== actor) throw new Error('Sign in with the account that saved this draft.');
  if (row.form_kind !== HUB_DRAFT_KIND || !object(row.payload) || row.payload.version !== 1)
    throw new Error('This draft needs manual recovery; its data was retained.');
  const payload = row.payload;
  if (
    (payload.activeForm !== null &&
      !['broiler', 'layer', 'egg', 'pig', 'cattle', 'sheep'].includes(payload.activeForm)) ||
    !object(payload.forms) ||
    !object(payload.extras) ||
    !object(payload.photoKeys) ||
    !['broiler', 'layer', 'egg', 'pig', 'cattle', 'sheep'].every((form) => object(payload.forms[form])) ||
    !['broiler', 'layer', 'pig'].every((form) => Array.isArray(payload.extras[form]))
  )
    throw new Error('This draft is incomplete; its data was retained.');
  const blobs = await listPhotoBlobsByCsid(csid);
  const photos = {} as HubDraft['photos'];
  let photoCount = 0;
  const seenKeys = new Set<string>();
  for (const form of PHOTO_FORMS) {
    if (!Array.isArray(payload.photoKeys[form])) throw new Error('Draft photo details are unavailable.');
    photos[form] = payload.photoKeys[form].map((key: unknown) => {
      if (typeof key !== 'string' || seenKeys.has(key))
        throw new Error('Draft photo details need review; all bytes were retained.');
      seenKeys.add(key);
      const stored = blobs.find((photo) => photo.photo_key === key);
      if (!stored) throw new Error('A draft photo is unavailable; the draft was retained.');
      photoCount++;
      return new File([stored.blob], stored.name || 'report-photo', {
        type: stored.mime,
        lastModified: Date.parse(stored.captured_at) || 0,
      });
    });
  }
  if (photoCount !== blobs.length) throw new Error('Draft photo details need review; all bytes were retained.');
  await assertHubDraftActor(sb, actor);
  return {...payload, photos} as HubDraft;
}

export const FLAT_HUB_FORM: Record<string, HubForm> = {
  poultry_dailys: 'broiler',
  pig_dailys: 'pig',
  cattle_dailys: 'cattle',
  sheep_dailys: 'sheep',
};
export type RecoveredInputRow = {
  feedId: string;
  qty?: string;
  lbs?: string;
  isCreep?: boolean;
  recoverySnapshot?: HubRecord;
  recoveryMissingInput?: boolean;
  recoveryUnitWeight?: number;
};

export function hydrateFlatHubForm(
  recovery: Pick<QueuedReportRecovery, 'formKind' | 'record'>,
  teamMember: string,
): {form: HubForm; values: HubRecord} {
  const form = FLAT_HUB_FORM[recovery.formKind];
  if (!form) throw new Error('Open this report in its original form.');
  const r = recovery.record;
  const base = {date: recoveryText(r.date), teamMember};
  const mapping =
    form === 'broiler'
      ? {
          batchLabel: 'batch_label',
          feedType: 'feed_type',
          feedLbs: 'feed_lbs',
          gritLbs: 'grit_lbs',
          mortalityCount: 'mortality_count',
          mortalityReason: 'mortality_reason',
          comments: 'comments',
        }
      : form === 'pig'
        ? {
            batchLabel: 'batch_label',
            feedLbs: 'feed_lbs',
            pigCount: 'pig_count',
            fenceVoltage: 'fence_voltage',
            issues: 'issues',
          }
        : form === 'cattle'
          ? {
              herd: 'herd',
              fenceVoltage: 'fence_voltage',
              mortalityCount: 'mortality_count',
              mortalityReason: 'mortality_reason',
              issues: 'issues',
            }
          : {flock: 'flock', fenceVoltageKv: 'fence_voltage_kv', comments: 'comments'};
  const values: HubRecord = {...base};
  for (const [key, column] of Object.entries(mapping)) values[key] = recoveryText(r[column]);
  const checks =
    form === 'broiler'
      ? {groupMoved: 'group_moved', watererChecked: 'waterer_checked'}
      : form === 'pig'
        ? {
            groupMoved: 'group_moved',
            nippleDrinkerMoved: 'nipple_drinker_moved',
            nippleDrinkerWorking: 'nipple_drinker_working',
            troughsMoved: 'troughs_moved',
            fenceWalked: 'fence_walked',
          }
        : form === 'cattle'
          ? {waterChecked: 'water_checked'}
          : {waterersWorking: 'waterers_working'};
  for (const [key, column] of Object.entries(checks)) values[key] = r[column];
  if (form === 'cattle' || form === 'sheep') {
    for (const field of ['feeds', 'minerals']) {
      const rows = r[field];
      if (!Array.isArray(rows) || !rows.every(object))
        throw new Error('Stored feed details need manual recovery; the queued report was retained.');
      values[field] = rows.length
        ? rows.map((row, index) => ({
            feedId: recoveryText(row.feed_input_id) || `__recovered-${field}-${index}`,
            ...(field === 'feeds'
              ? {qty: recoveryText(row.qty), isCreep: !!row.is_creep}
              : {lbs: recoveryText(row.lbs)}),
            recoverySnapshot: row,
          }))
        : [{feedId: '', ...(field === 'feeds' ? {qty: ''} : {lbs: ''})}];
    }
  }
  return {form, values};
}

// Frozen snapshots win for the original selected input, including inputs no
// longer in today's master. An explicit different selection uses today's
// ordinary form builder. Quantity edits keep the old conversion/nutrition.
export function recoveredInputPayload(row: RecoveredInputRow, mineral = false): HubRecord | undefined {
  if (row.recoveryMissingInput)
    throw new Error('Choose this saved feed or mineral again; its original input details are unavailable.');
  const saved = row.recoverySnapshot;
  if (!saved || (row.feedId !== saved.feed_input_id && !row.feedId.startsWith('__recovered-'))) return undefined;
  const amount = Number(mineral ? row.lbs : row.qty);
  const oldAmount = Number(mineral ? saved.lbs : saved.qty);
  if (mineral) return amount === oldAmount ? saved : {...saved, lbs: amount};
  const creep = saved.is_creep === undefined ? {} : {is_creep: !!row.isCreep};
  if (amount === oldAmount)
    return saved.is_creep === undefined || !!row.isCreep === !!saved.is_creep ? saved : {...saved, ...creep};
  const unitWeight = oldAmount > 0 ? Number(saved.lbs_as_fed) / oldAmount : row.recoveryUnitWeight;
  if (!Number.isFinite(amount) || !Number.isFinite(unitWeight))
    throw new Error('Choose this feed again before changing its quantity; the saved conversion is unavailable.');
  return {
    ...saved,
    qty: amount,
    lbs_as_fed: Math.round(amount * Number(unitWeight) * 100) / 100,
    ...creep,
  };
}

// Use the ordinary form payload builder when freezing an unsent local draft.
// A recovered selection keeps the accepted snapshot; a deliberately changed
// selection uses the current master. Missing saved inputs must not vanish.
export function buildHubInputPayload(
  row: RecoveredInputRow,
  inputs: HubRecord[],
  form: 'cattle' | 'sheep',
  mineral = false,
): HubRecord | null {
  const frozen = recoveredInputPayload(row, mineral);
  if (frozen) return frozen;
  const fi = inputs.find((input) => input.id === row.feedId);
  if (!fi) return null;
  if (mineral)
    return {
      feed_input_id: fi.id,
      name: fi.name,
      lbs: form === 'cattle' ? parseFloat(row.lbs ?? '') || 0 : parseFloat(row.lbs ?? ''),
    };
  const qty = parseFloat(row.qty ?? '') || 0;
  const result: HubRecord = {
    feed_input_id: fi.id,
    feed_name: fi.name,
    category: fi.category,
    qty,
    unit: fi.unit,
    lbs_as_fed: Math.round(qty * (parseFloat(fi.unit_weight_lbs) || 1) * 100) / 100,
    is_creep: form === 'cattle' ? !!row.isCreep : false,
  };
  if (form === 'cattle')
    result.nutrition_snapshot = {
      moisture_pct: fi.moisture_pct,
      nfc_pct: fi.nfc_pct,
      protein_pct: fi.protein_pct,
    };
  return result;
}

export function freezeHubInputs(values: HubRecord, inputs: HubRecord[], form: 'cattle' | 'sheep'): HubRecord {
  const frozen = {...values};
  for (const field of ['feeds', 'minerals']) {
    frozen[field] = (values[field] || []).map((row: RecoveredInputRow) => {
      if (!row.feedId || row.recoverySnapshot || row.recoveryMissingInput) return row;
      const snapshot = buildHubInputPayload(row, inputs, form, field === 'minerals');
      return snapshot
        ? {
            ...row,
            recoverySnapshot: snapshot,
            recoveryUnitWeight: parseFloat(inputs.find((input) => input.id === row.feedId)?.unit_weight_lbs) || 1,
          }
        : {...row, recoveryMissingInput: true};
    });
  }
  return frozen;
}

export function withRecoveredInputChoice(options: HubRecord[], row: RecoveredInputRow): HubRecord[] {
  const saved = row.recoverySnapshot;
  if (row.recoveryMissingInput && !options.some((option) => option.id === row.feedId))
    return [...options, {id: row.feedId, name: 'Saved input (unavailable)'}];
  if (!saved || (row.feedId !== saved.feed_input_id && !row.feedId.startsWith('__recovered-'))) return options;
  const choice = {
    id: row.feedId,
    name: saved.feed_name ?? saved.name ?? row.feedId,
    unit: saved.unit,
    category: saved.category,
    unit_weight_lbs: Number(saved.qty) > 0 ? Number(saved.lbs_as_fed) / Number(saved.qty) : undefined,
  };
  return options.some((option) => option.id === row.feedId)
    ? options.map((option) => (option.id === row.feedId ? {...option, ...choice} : option))
    : [...options, choice];
}

// ============================================================================
// Stamped CAS draft save — the editing-session lifecycle owner
// ============================================================================
// The coalesced, mint-free save the Hub's editing lifecycle drives. Identity
// is stable per editing instance: the draft key derives from the instance id,
// so a save NEVER mints a submission id, a second tab or a remount forks to
// its own key instead of overwriting, and every write is a stamped
// compare-and-swap over submissions + original photo bytes in one
// transaction. Legacy unstamped drafts (created by older shipped code at
// random csids) can never collide with an instance key, so they are simply
// retained — list/load/discard keep working on them unchanged.
export type HubDraftSaveResult =
  | {kind: 'saved'; csid: string; stamp: LocalDraftStamp}
  | {kind: 'unchanged'; csid: string; stamp: LocalDraftStamp | null}
  | {kind: 'conflict'; stamp: LocalDraftStamp | null}
  | {kind: 'not-current'}
  | {kind: 'failed'; message: string};

export type HubDraftClearResult = 'deleted' | 'kept' | 'absent' | 'not-current' | 'failed';

const DRAFT_KEPT_MESSAGE = 'Saved on this device for review. Open to continue; nothing is sent automatically.';

export function newHubDraftInstanceId(): string {
  return crypto.randomUUID();
}

export function hubDraftCsidFor(instanceId: string): string {
  return `${HUB_DRAFT_KIND}-instance-${instanceId}`;
}

/** The instance id behind a stamped-session draft key, or null for any other
 * row — legacy preserve-path drafts live at random csids and have no session
 * chain to continue. */
export function hubDraftInstanceIdOf(csid: string): string | null {
  const prefix = `${HUB_DRAFT_KIND}-instance-`;
  return csid.startsWith(prefix) ? csid.slice(prefix.length) : null;
}

/** Custody facts of a saved Hub draft row, read for the Resume handoff: the
 * row's CURRENT CAS stamp (null for legacy unstamped rows) and a content
 * identity (the stamped content hash, else the preserve signature) that a
 * superseded legacy row must still carry to be retired. Null when the row is
 * no longer this actor's Hub draft. */
export async function loadHubDraftCustody(
  sb: SupabaseClient,
  csid: string,
): Promise<{stamp: LocalDraftStamp | null; contentIdentity: string | null} | null> {
  const actor = await queuedReportActor(sb);
  const row = await getSubmission(csid);
  if (!actor || row?.actor_profile_id !== actor || row.form_kind !== HUB_DRAFT_KIND) return null;
  const record = object(row.record) ? row.record : {};
  const contentIdentity =
    typeof record.content_hash === 'string'
      ? record.content_hash
      : typeof record.signature === 'string'
        ? record.signature
        : null;
  return {stamp: localDraftStampOf(row), contentIdentity};
}

// The one stamped write shape: the version-1 payload and its photo keys, the
// ORIGINAL bytes with their digests, and the canonical content hash over
// (actor, payload, photo digests). The editing session's save and the
// teardown carrier both build it here, so identical content under the same
// author hashes identically — an adopted carrier chain's next save can take
// the unchanged fast path.
async function hubDraftWriteParts(snapshot: HubDraft, csid: string, actor: string) {
  const photoEntries = PHOTO_FORMS.flatMap((form) => snapshot.photos[form].map((file, index) => ({form, index, file})));
  const photos = await Promise.all(
    photoEntries.map(async ({form, index, file}) => ({
      photo_key: `${form}-${index}`,
      path: `${HUB_DRAFT_KIND}/${csid}/${form}-${index}`,
      blob: file,
      name: file.name,
      mime: file.type,
      size_bytes: file.size,
      captured_at: new Date(file.lastModified || 0).toISOString(),
      digest: await fileSha256Hex(file),
    })),
  );
  const payload = {
    ...snapshot,
    photos: undefined,
    photoKeys: Object.fromEntries(
      PHOTO_FORMS.map((form) => [form, snapshot.photos[form].map((_, index) => `${form}-${index}`)]),
    ),
    version: 1,
  };
  const contentHash = await sha256Hex(
    sortedHubDraftJson({
      actor,
      payload,
      photos: photos.map(({photo_key, digest}) => ({photo_key, digest})),
    }),
  );
  return {photos, payload, contentHash};
}

export async function saveHubDraftCas(
  sb: SupabaseClient,
  snapshot: HubDraft,
  options: {
    instanceId: string;
    expectStamp: LocalDraftStamp | null;
    stillCurrent: () => boolean;
    /** The author bound by the caller BEFORE scheduling. After this module's
     * own actor read resolves, a mismatch refuses truthfully: A's snapshot is
     * never committed under B's account. */
    expectedActor?: string | null;
  },
): Promise<HubDraftSaveResult> {
  try {
    const actor = await queuedReportActor(sb);
    if (!actor) return {kind: 'failed', message: 'Sign in to keep this draft on this device.'};
    if (options.expectedActor != null && actor !== options.expectedActor) {
      return {
        kind: 'failed',
        message: 'The signed-in account changed while saving. Your edits are still on the form; nothing was saved.',
      };
    }
    // A snapshot the structured clone cannot carry (a stray function, a
    // non-cloneable value) can never commit to IndexedDB either. Refuse it
    // BEFORE hashing, so it is never mistaken for unchanged content: the
    // canonical hash would silently drop such a field.
    structuredClone(snapshot);
    if (!options.stillCurrent()) return {kind: 'not-current'};
    const csid = hubDraftCsidFor(options.instanceId);
    const {photos, payload, contentHash} = await hubDraftWriteParts(snapshot, csid, actor);
    // Unchanged fast path: same content under the stamp we already know —
    // no write, no bytes rewritten, nothing minted. Still gated by the
    // caller's currency: an outdated attempt never rides the fast path past
    // a newer revision.
    const existing = await getSubmission(csid);
    if (existing && object(existing.record) && existing.record.content_hash === contentHash) {
      const stored = localDraftStampOf(existing);
      if (
        stored &&
        options.expectStamp &&
        stored.mountId === options.expectStamp.mountId &&
        stored.revision === options.expectStamp.revision
      ) {
        if (!options.stillCurrent()) return {kind: 'not-current'};
        return {kind: 'unchanged', csid, stamp: stored};
      }
    }
    const stamp: LocalDraftStamp = {
      // A3: mountId is the CHAIN identity and is never reused across a
      // delete/recreate. A fresh chain (expectStamp null — e.g. after an own
      // clear nulled the session stamp) allocates a fresh nonce, so a stale
      // holder of an old same-numbered revision can never match the new
      // chain's CAS. An existing chain retains the mountId it was verified
      // against. Storage keys and the read shape are unchanged.
      mountId: options.expectStamp ? options.expectStamp.mountId : crypto.randomUUID(),
      revision: (options.expectStamp?.revision ?? 0) + 1,
    };
    const result = await casLocalDraftWithPhotos({
      formKind: HUB_DRAFT_KIND,
      csid,
      actorProfileId: actor,
      expectStamp: options.expectStamp,
      stamp,
      payload: {...payload, contentHash},
      lastError: DRAFT_KEPT_MESSAGE,
      photos,
      stillCurrent: options.stillCurrent,
    });
    if (result.status === 'written') return {kind: 'saved', csid, stamp};
    if (result.status === 'conflict') return {kind: 'conflict', stamp: result.stamp};
    return {kind: 'not-current'};
  } catch (error) {
    if ((error as {name?: string})?.name === 'DraftNotCurrentAbort') return {kind: 'not-current'};
    // Quota, IDB unavailability, private-mode failures: the prior durable
    // snapshot and photos are untouched; the caller reports this truthfully
    // and keeps the form editable.
    return {kind: 'failed', message: error instanceof Error ? error.message : 'Could not save this draft locally.'};
  }
}

/**
 * Clear the editing instance's draft only while it still carries exactly the
 * stamp the caller captured (the confirmed submit's revision). A newer
 * revision — anything typed since Submit, another tab, another instance —
 * owns the key then, and 'kept' leaves it untouched.
 *
 * Authorization is the caller's synchronous `stillCurrent` — which includes
 * the auth-memory identity check — evaluated by the queue before the delete,
 * after the index read, and after the deletes, with no await inside the
 * transaction. There is deliberately NO live-SDK read here: an awaited call
 * inside an open IndexedDB transaction would let it auto-commit.
 */
export async function clearHubDraftCas(
  sb: SupabaseClient,
  options: {
    csid: string;
    stamp: LocalDraftStamp;
    stillCurrent: () => boolean;
    /** The author captured when the clear was scheduled. When given, it is
     * the deleting actor directly (the row owner); the live account is still
     * guarded by the caller's stillCurrent auth-memory check. */
    actorProfileIdOverride?: string;
  },
): Promise<HubDraftClearResult> {
  try {
    const actor = options.actorProfileIdOverride ?? (await queuedReportActor(sb));
    if (!actor) return 'failed';
    const result = await deleteLocalDraftWithPhotosIfStamp({
      formKind: HUB_DRAFT_KIND,
      csid: options.csid,
      actorProfileId: actor,
      stamp: options.stamp,
      stillCurrent: options.stillCurrent,
    });
    return result === 'not_current' ? 'not-current' : result;
  } catch (error) {
    if ((error as {name?: string})?.name === 'DraftNotCurrentAbort') return 'not-current';
    return 'failed';
  }
}

// ── Teardown carrier custody ────────────────────────────────────────────────
// When the Hub unmounts (the App drops it on an account change) with owned
// work its session never made durable, hubDraftCarrier.ts keeps that captured
// snapshot. These two reads/writes are its only storage boundary: the same
// review-only stamped row and original bytes as the session's own save, at a
// FRESH instance key, under the account that TYPED it. That owner is passed
// in — captured when the work was typed — and never read from the live
// session, so A's snapshot is never relabelled as B. No submission id, no
// send, no drain, no clear; the source session's row is never touched.

export type HubDraftCarrierMeta = {
  sourceCsid: string | null;
  sourceStamp: LocalDraftStamp | null;
  edits: number;
  capturedAt: string;
  reason: 'teardown';
  /** A tombstone: the owner had emptied the saved draft at `csid` and its
   * owned clear of exactly `stamp` had not settled. The row's snapshot is
   * that empty content; nothing was cleared at teardown. */
  removal: {csid: string; stamp: LocalDraftStamp} | null;
};

/** The removal a loaded carrier draft still holds, or null for anything
 * else (an ordinary draft, a content carrier, a malformed value). */
export function hubCarrierRemovalOf(draft: unknown): {csid: string; stamp: LocalDraftStamp} | null {
  const carrier = object(draft) ? draft.carrier : null;
  const removal = object(carrier) ? carrier.removal : null;
  if (!object(removal) || typeof removal.csid !== 'string' || !hubDraftInstanceIdOf(removal.csid)) return null;
  const stamp = removal.stamp;
  if (!object(stamp) || typeof stamp.mountId !== 'string' || !Number.isInteger(stamp.revision)) return null;
  return {csid: removal.csid, stamp: {mountId: stamp.mountId, revision: stamp.revision}};
}

export const HUB_CARRIER_KEPT_MESSAGE =
  'Kept on this device when the page closed. Open to continue; nothing is sent automatically.';

/** True only when `csid` already holds exactly this owner's snapshot (same
 * canonical content hash): the carrier then has nothing to keep. */
export async function hubDraftHeldDurably(csid: string, owner: string, snapshot: HubDraft): Promise<boolean> {
  const row = await getSubmission(csid);
  if (!row || row.form_kind !== HUB_DRAFT_KIND || row.actor_profile_id !== owner || !object(row.record)) return false;
  const {contentHash} = await hubDraftWriteParts(snapshot, csid, owner);
  return row.record.content_hash === contentHash;
}

export async function writeHubDraftCarrierRow(
  snapshot: HubDraft,
  options: {owner: string; csid: string; meta: HubDraftCarrierMeta; stillCurrent: () => boolean},
): Promise<{status: 'written'; stamp: LocalDraftStamp} | {status: 'conflict'} | {status: 'not-current'}> {
  if (!options.owner) throw new Error('This draft has no known author; nothing was saved.');
  // As the session save: a value IndexedDB cannot clone is refused before
  // hashing, never mistaken for kept content.
  structuredClone(snapshot);
  const {photos, payload, contentHash} = await hubDraftWriteParts(snapshot, options.csid, options.owner);
  if (!options.stillCurrent()) return {status: 'not-current'};
  const stamp: LocalDraftStamp = {mountId: crypto.randomUUID(), revision: 1};
  const result = await casLocalDraftWithPhotos({
    formKind: HUB_DRAFT_KIND,
    csid: options.csid,
    actorProfileId: options.owner,
    // A fresh key: the write succeeds only where no row exists, so it can
    // never overwrite another draft.
    expectStamp: null,
    stamp,
    payload: {...payload, contentHash, carrier: options.meta},
    lastError: HUB_CARRIER_KEPT_MESSAGE,
    photos,
    stillCurrent: options.stillCurrent,
  });
  if (result.status === 'written') return {status: 'written', stamp};
  if (result.status === 'conflict') return {status: 'conflict'};
  return {status: 'not-current'};
}

/**
 * Product rule (Hub Qualification Run 10):
 * A saved Hub draft is listed for auto-open on a subform only when it holds content
 * for THAT specific form (answers beyond blank/defaults, extra groups, or photos),
 * judged by the component's own definition of content: isPristineForm(form, freshTemplate, extras).
 *
 * Fail-open: If the row payload cannot be read, has no per-form structure (e.g. carrier
 * memory rows, legacy drafts), has a missing form or missing fresh template, or represents a
 * genuine stuck submission (form_kind !== HUB_DRAFT_KIND), it returns true so that potential
 * user work is never hidden.
 */
export function draftHasFormAnswers(
  row: unknown,
  form: string,
  freshTemplates?: Record<string, unknown> | null,
): boolean {
  if (!row || typeof row !== 'object') return true;
  const r = row as Record<string, unknown>;
  if (r.form_kind && r.form_kind !== HUB_DRAFT_KIND) return true;
  if (!r.payload || typeof r.payload !== 'object') return true;
  const payload = r.payload as Record<string, unknown>;

  // If carrier row without an accessible snapshot, fail open
  if (payload.carrier && typeof payload.carrier === 'object') {
    const carrier = payload.carrier as Record<string, unknown>;
    if (!carrier.snapshot || typeof carrier.snapshot !== 'object') return true;
  }

  const snapshot = (payload.snapshot && typeof payload.snapshot === 'object' ? payload.snapshot : payload) as Record<
    string,
    unknown
  >;

  const forms = snapshot.forms;
  if (!forms || typeof forms !== 'object') return true;
  const formData = (forms as Record<string, unknown>)[form];
  if (!formData || typeof formData !== 'object') return true;

  // Missing fresh template for this form: fail open
  const fresh = (freshTemplates as Record<string, Record<string, unknown>> | null | undefined)?.[form];
  if (!fresh || typeof fresh !== 'object') return true;

  // Extra groups for this form
  const extras = snapshot.extras as Record<string, unknown> | undefined;
  const extraGroups = extras && Array.isArray(extras[form]) ? extras[form].length : 0;

  // Photos for this form (both photoKeys and photos structures checked)
  const photoKeys = snapshot.photoKeys as Record<string, unknown> | undefined;
  const photosArr = snapshot.photos as Record<string, unknown> | undefined;
  const photosCount =
    (photoKeys && Array.isArray(photoKeys[form]) ? photoKeys[form].length : 0) ||
    (photosArr && Array.isArray(photosArr[form]) ? photosArr[form].length : 0);

  // Single definition of content: reuse isPristineForm
  return !isPristineForm(formData as Record<string, unknown>, fresh, {
    extraGroups,
    photos: photosCount,
  });
}
